/**
 * Every fixed string the diagnostic shows, in one place.
 *
 * Question text, chip labels and reflections are editorial data and come from
 * the backend's questions payload — they are deliberately absent here. What
 * lives in this file is the chrome around them: the intro, the composer, the
 * closing line and the result headlines, taken from the Hard-Wired diagnostic
 * copy deck.
 */

export const LOADING = "Loading the diagnostic";
export const LOAD_ERROR = "We could not load the diagnostic. Please refresh.";

export const INTRO_EYEBROW = "Hard-Wired diagnostic";

export const INTRO_HEADLINE_LEAD = "Tell us how work really happens.";
export const INTRO_HEADLINE_TAIL = "We'll show you the wiring.";

export const INTRO_LEDE =
  "Six questions about how your organization really works. Pick the answer closest to the truth. We score you on the three tracks from Hard-Wired and draw your organization's wiring as you go.";

export const INTRO_START = "Start the diagnostic";

export const INTRO_TIME_NOTE = "About 3 minutes. You see your score before we ask for anything.";

/** The textarea's accessible name on single-select questions (no visible label). */
export const COMPOSER_LABEL_SR = "Your answer, optional detail";
export const COMPOSER_LABEL_MULTI = "Select all that apply, then continue";

export const COMPOSER_PLACEHOLDER = "Optional: add context in your own words.";

export const COMPOSER_SEND = "Send";
export const COMPOSER_CONTINUE = "Continue";

/** Shown under the multi-select chips while the answer is about to send itself. */
export const AUTO_CONTINUE_HINT =
  "Continuing in a moment. Keep selecting, or tap Continue to go now.";

export const CLOSING =
  "That's everything. Your wiring portrait is ready. Hover over any band to see your answer.";

export const RESULT_INDEX_LABEL = "Your AI-first index";

/**
 * One fixed headline per level, keyed by the level label the questions payload
 * serves. Keeping the key editorial means a renamed band shows up here as a
 * miss rather than as silently wrong copy.
 */
const RESULT_HEADLINES: Record<string, string> = {
  "Hard-wired": "Hard-wired. The old way is still the easiest way.",
  Rewiring: "Rewiring. The new way exists. It isn't the default yet.",
  "AI-first by default": "AI-first by default. Now keep the old path closed.",
};

/** The result headline for a level label, falling back to the label itself. */
export function resultHeadline(level: string): string {
  return RESULT_HEADLINES[level] ?? (level ? `${level}.` : "");
}

/** The line under the result headline, naming the lowest-scoring track. */
export function weakestTrackLine(trackInSentence: string): string {
  return `Your weakest track: ${trackInSentence}.`;
}

// ─── Screen 4: the gate ──────────────────────────────────────────────────

export const GATE_HEADING = "Get your full diagnostic";

export const GATE_LEDE =
  "A diagnosis per track, three moves for your weakest track, and the Hard-Wired whitepaper.";

export const GATE_FIRST_NAME = "First name";
export const GATE_LAST_NAME = "Last name";
export const GATE_EMAIL = "Work email";
export const GATE_COMPANY = "Company";
export const GATE_ROLE = "Role";
export const GATE_ROLE_PLACEHOLDER = "Select";

/**
 * The consent notice, split around the privacy policy link. Submitting the
 * form is the consent, so there is no checkbox. The backend records the same
 * wording with the submission (CONSENT_TEXT in diagnostic/hubspot.py), so the
 * two change together.
 */
export const GATE_FINE_PRINT_LEAD =
  "By clicking submit below, you agree that you have read and understood our ";
export const GATE_PRIVACY_LINK = "Privacy Policy";
export const GATE_FINE_PRINT_TAIL =
  " and consent to allow Board of Innovation to store and process the personal information submitted above to provide you with the content requested. You may unsubscribe from these communications at any time.";

export const GATE_SUBMIT = "Show my diagnostic";
export const GATE_PREPARING = "Preparing your diagnostic";
export const GATE_RETRY = "Retry";

export const GATE_FIRST_NAME_REQUIRED = "Please add your first name.";
export const GATE_LAST_NAME_REQUIRED = "Please add your last name.";
export const GATE_EMAIL_REQUIRED = "Please add your work email.";
export const GATE_EMAIL_INVALID = "That email address does not look right.";
export const GATE_COMPANY_REQUIRED = "Please add your company.";
export const GATE_ROLE_REQUIRED = "Please pick the closest role.";

export const GATE_FREE_EMAIL_NOTE =
  "That looks like a personal address. A work email helps us send the right follow-up.";

export const GATE_RATE_LIMITED =
  "You have requested a few diagnostics already. Please try again in an hour.";
export const GATE_FAILED = "We could not write your diagnostic. Please try again.";

// ─── Screen 5: the report ────────────────────────────────────────────────

/** The report's eyebrow, which is also what takes focus when it appears. */
export function reportEyebrow(firstName: string): string {
  return `${firstName}, your diagnostic`;
}

export const REPORT_MOVES = "Your three moves";
export const REPORT_WHITEPAPER = "Read the Hard-Wired whitepaper";
export const REPORT_GATE_FRAMEWORK = "GATE framework";

// ─── The portrait poster ─────────────────────────────────────────────────

export const POSTER_DOWNLOAD = "Download my portrait";
export const POSTER_PREPARING = "Preparing your poster";
export const POSTER_FAILED = "We could not make your poster. Please try again.";

/** The poster's eyebrow, in caps above the title. */
export const POSTER_EYEBROW = "HARD-WIRED · AI DIAGNOSTIC";

/** The poster's footer line. */
export const POSTER_FOOTER = "boardofinnovation.com";

/** The wordmark drawn when `/brand/logo.svg` cannot be loaded into a canvas. */
export const POSTER_WORDMARK = "Board of Innovation";

/** What the poster calls an organization the gate did not name. */
export const POSTER_FALLBACK_COMPANY = "your organization";

/** The poster's title, with the company the gate collected. */
export function posterTitle(company: string): string {
  const named = company.trim() || POSTER_FALLBACK_COMPANY;
  return `How hard-wired is ${named}?`;
}

/** The two-digit row number a track row leads with: `01`, `02`, `03`. */
export function trackNumber(track: number): string {
  return String(track + 1).padStart(2, "0");
}

// ─── Outbound links ──────────────────────────────────────────────────────

export const PRIVACY_URL = "https://www.boardofinnovation.com/privacy-policy/";
export const GATE_FRAMEWORK_URL = "https://www.boardofinnovation.com/tools/gate-framework/";
export const WHITEPAPER_URL = "https://www.boardofinnovation.com/hardwired-whitepaper/";
